import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

export type DeviceReadinessRow = {
  deviceId: string;
  label: string;
  preflightStatus: 'passed' | 'not_ready' | 'unknown';
  rolloutStatus: 'converged' | 'pending' | 'drifted' | 'unknown';
  countsTowardSiteGate?: boolean;
};

export interface SetupRolloutDeviceReadinessAccordionProps {
  deviceStates: DeviceReadinessRow[];
  evidenceLoading: boolean;
  onInspectDevice: (deviceId: string) => void;
}

export const SetupRolloutDeviceReadinessAccordion: React.FC<SetupRolloutDeviceReadinessAccordionProps> = ({
  deviceStates,
  evidenceLoading,
  onInspectDevice,
}) => {
  if (!deviceStates.length) return null;

  const gateDevices = deviceStates.filter((d) => d.countsTowardSiteGate !== false);
  const passed = gateDevices.filter((d) => d.preflightStatus === 'passed').length;
  const excludedCount = deviceStates.length - gateDevices.length;

  return (
    <Accordion defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 700 }}>Device readiness</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          {passed}/{gateDevices.length || deviceStates.length} in-service passed preflight
          {excludedCount > 0 ? ` (${excludedCount} offline excluded)` : ''}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
          <Box
            sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: 'minmax(0, 2fr) minmax(140px, 1fr) minmax(140px, 1fr) auto',
              gap: 2,
              px: 1.5,
              py: 1,
              bgcolor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Device
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Setup readiness
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Rollout
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Action
            </Typography>
          </Box>
          <Stack divider={<Divider flexItem />}>
            {deviceStates.map((device) => {
              const outOfGate = device.countsTowardSiteGate === false;
              const rolloutColor = device.rolloutStatus === 'converged'
                ? 'success'
                : device.rolloutStatus === 'pending'
                  ? 'warning'
                  : device.rolloutStatus === 'drifted'
                    ? 'error'
                    : 'default';
              return (
                <Box
                  key={device.deviceId}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(140px, 1fr) minmax(140px, 1fr) auto' },
                    gap: 1.5,
                    alignItems: 'center',
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                      {device.label}
                    </Typography>
                    {outOfGate && (
                      <Chip size="small" variant="outlined" label="Offline — excluded from site gate" sx={{ mt: 0.5 }} />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' } }}>
                      Edge box readiness
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, mb: 0.25 }}>
                      Setup readiness
                    </Typography>
                    <Chip
                      size="small"
                      color={device.preflightStatus === 'passed' ? 'success' : device.preflightStatus === 'not_ready' ? 'warning' : 'default'}
                      label={outOfGate
                        ? 'Excluded'
                        : evidenceLoading
                        ? 'Checking'
                        : device.preflightStatus === 'passed'
                          ? 'Passed'
                          : device.preflightStatus === 'not_ready'
                            ? 'Blocked'
                            : 'Unavailable'}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, mb: 0.25 }}>
                      Rollout
                    </Typography>
                    <Chip
                      size="small"
                      color={rolloutColor}
                      label={outOfGate ? 'n/a' : device.rolloutStatus}
                    />
                  </Box>
                  <Box sx={{ justifySelf: { md: 'end' } }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => onInspectDevice(device.deviceId)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Inspect device
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
